import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {useMemo} from 'react';

import * as Filters from '../filters';
import type {Filter} from '../filterTypes';
import {runIdsToNames} from '../selectionmanager';

const RUN_NAME = Filters.Key('run', 'name');

/**
 * Generates a filter that excludes pinned runs. This is important because the
 * main run selector query should not return runs that are already pinned or set
 * as the baseline, because those are queried separately.
 *
 * @param pinnedRunIds
 * @return Filter that excludes pinned and baseline runs, or null if there
 *         are no pinned or baseline runs
 */
export function excludedRunIdsFilter(
  pinnedRunIds: string[]
): Filter<any> | null {
  if (pinnedRunIds.length === 0) {
    return null;
  }

  return Filters.NotIn(RUN_NAME, runIdsToNames(pinnedRunIds));
}

/**
 * Generates the filter that selects any pinned or baseline or pinned ids
 * passed to the function, returning an empty Or clause if it's given no ids.
 *
 * @param pinnedRunIds
 * @return Filter that selects pinned and baseline runs, returning an empty
 *         Or clause if no ids are passed.
 */
export function pinnedRunIdsFilter(pinnedRunIds: string[]): Filter<any> {
  if (pinnedRunIds.length === 0) {
    return Filters.Or([]);
  }

  return Filters.Or(
    runIdsToNames(pinnedRunIds).map(name => Filters.Eq(RUN_NAME, name))
  );
}

type PinnedAndBaselineRunIds = {
  pinnedRunIds?: string[];
  baselineRunId?: string;
};

type UsePinnedRunFiltersForRunSetsHook = {
  excludedIDsFilter: ReturnType<typeof excludedRunIdsFilter>;
  pinnedRunsFilter: ReturnType<typeof pinnedRunIdsFilter>;
};

export function usePinnedRunFiltersForRunSets(
  runSets: PinnedAndBaselineRunIds[] = []
): UsePinnedRunFiltersForRunSetsHook {
  const pinnedRuns = useMemo(
    () => runSets.flatMap(rs => rs.pinnedRunIds ?? []),
    [runSets]
  );
  const baselineRuns = useMemo(
    () => runSets.map(rs => rs.baselineRunId).filter(isNotNullOrUndefined),
    [runSets]
  );
  const excludedIDsFilter = useMemo(() => {
    return excludedRunIdsFilter(baselineRuns.concat(pinnedRuns));
  }, [baselineRuns, pinnedRuns]);

  const pinnedRunsFilter = useMemo(() => {
    return pinnedRunIdsFilter(baselineRuns.concat(pinnedRuns));
  }, [baselineRuns, pinnedRuns]);

  return {excludedIDsFilter, pinnedRunsFilter};
}

export function usePinnedRunFilters(
  pinnedRunIds: string[],
  baselineRunId?: string
): UsePinnedRunFiltersForRunSetsHook {
  return usePinnedRunFiltersForRunSets(
    useMemo(
      () => [{pinnedRunIds, baselineRunId}],
      [pinnedRunIds, baselineRunId]
    )
  );
}
